import React, { useEffect, useState } from 'react';
import {
    Button,
    TextField,
    Box,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import Modal from '../../components/Modals/Modal';
import { showMessage } from '../../utils/helper';
import { useDebounce } from '../../hooks/useDebounce';

function AgencySingleUploadModal({
    open,
    onClose,
    dispatchUploadSingleLead,
    hubList,
    cityList,
    dispatchFetchLookup,
    onSuccess,
}) {
    const { debounce, isBouncing } = useDebounce();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: '',
        contactNumber: '',
        preferredStore: '',
        cityId: '',
        zoneId: '',
    });
    const [zoneList, setZoneList] = useState([]);
    const [cityStoreList, setCityStoreList] = useState(null);

    // Zones and stores narrow to the chosen city; with no city, any store can be picked (as before)
    useEffect(() => {
        setZoneList([]);
        setCityStoreList(null);
        if (!form.cityId) return;
        let active = true; // ignore replies for a city the user already moved away from
        dispatchFetchLookup('zones', form.cityId, (resp) => active && setZoneList(resp?.response?.items ?? []));
        dispatchFetchLookup('stores', form.cityId, (resp) => active && setCityStoreList(resp?.response?.items ?? []));
        return () => {
            active = false;
        };
    }, [form.cityId]);

    const storeOptions = cityStoreList ?? hubList ?? [];

    const handleClose = () => {
        setForm({ name: '', contactNumber: '', preferredStore: '', cityId: '', zoneId: '' });
        onClose && onClose();
    };

    const handleSubmit = () => {
        if (!form.contactNumber || form.contactNumber.length !== 10) {
            showMessage('Contact number must be exactly 10 digits', 'error');
            return;
        }
        const trimmedName = form.name.trim();
        debounce(() => {
            setLoading(true);
            dispatchUploadSingleLead(
                {
                    name: trimmedName || null,
                    contactNumber: form.contactNumber,
                    preferredStore: form.preferredStore ? Number(form.preferredStore) : null,
                    cityId: form.cityId || null,
                    zoneId: form.zoneId || null,
                },
                () => {
                    setLoading(false);
                    showMessage('Lead added successfully', 'success');
                    onSuccess && onSuccess();
                },
                () => setLoading(false), // middleware already shows the API's reason
            );
        });
    };

    const actions = [
        <Button
            key="cancel"
            variant="contained"
            color="default"
            className="cancelButton"
            onClick={handleClose}
            disableElevation
        >
            Cancel
        </Button>,
        <Button
            key="submit"
            variant="contained"
            color="primary"
            className="saveButton"
            disabled={isBouncing || loading || form.contactNumber.length !== 10}
            onClick={handleSubmit}
            disableElevation
        >
            Add Lead
        </Button>,
    ];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Add Agency Lead"
            actions={actions}
            loading={loading}
        >
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px' }}>
                <TextField
                    label="Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <TextField
                    label="Contact Number *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={form.contactNumber}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) =>
                        setForm({ ...form, contactNumber: e.target.value.replace(/\D/g, '') })
                    }
                />
                <Autocomplete
                    options={cityList ?? []}
                    getOptionLabel={(city) => city.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    value={(cityList ?? []).find((c) => String(c.id) === String(form.cityId)) || null}
                    onChange={(_, newValue) =>
                        setForm({
                            ...form,
                            cityId: newValue ? String(newValue.id) : '',
                            zoneId: '',
                            preferredStore: '',
                        })
                    }
                    renderInput={(params) => (
                        <TextField {...params} label="City" variant="outlined" size="small" fullWidth />
                    )}
                />
                <Autocomplete
                    options={zoneList}
                    disabled={!form.cityId}
                    getOptionLabel={(zone) => zone.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    value={zoneList.find((z) => String(z.id) === String(form.zoneId)) || null}
                    onChange={(_, newValue) =>
                        setForm({ ...form, zoneId: newValue ? String(newValue.id) : '' })
                    }
                    renderInput={(params) => (
                        <TextField {...params} label="Zone" variant="outlined" size="small" fullWidth />
                    )}
                />
                <Autocomplete
                    options={storeOptions}
                    getOptionLabel={(hub) => `${hub.name} (ID: ${hub.id})`}
                    getOptionSelected={(option, value) => option.id === value.id}
                    value={
                        storeOptions.find(
                            (hub) => String(hub.id) === String(form.preferredStore),
                        ) || null
                    }
                    onChange={(_, newValue) =>
                        setForm({
                            ...form,
                            preferredStore: newValue ? String(newValue.id) : '',
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Store"
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                    )}
                />
            </Box>
        </Modal>
    );
}

export default AgencySingleUploadModal;
